/**
 * WsTokenRefresher
 *
 * Listens for 'auth-refresh' events and updates the auth token in all registered
 * WebsocketProvider instances. provider.url is a computed getter built from
 * provider.params, so we mutate provider.params['token'] directly. y-websocket
 * reads params on every reconnect attempt, so the next retry will use the
 * updated token automatically.
 *
 * Only providers whose URL already contains a `token` param are updated. Those
 * created without getWsAuthParams() (no auth requirement) are silently skipped.
 *
 * Registration also forwards the provider to wsReconnectCoordinator so every
 * auth-aware provider gets tab-visibility-aware reconnect coordination. This
 * is the canonical single point for "a new WebsocketProvider now exists" —
 * keeping both concerns routed through it avoids boilerplate at every call site.
 */
import type { WebsocketProvider } from 'y-websocket';
import { wsReconnectCoordinator } from './wsReconnectCoordinator';

class WsTokenRefresher {
  private readonly providers = new Set<WebsocketProvider>();

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('auth-refresh', () => this.handleTokenRefresh());
    }
  }

  register(provider: WebsocketProvider): void {
    this.providers.add(provider);
    wsReconnectCoordinator.register(provider);
  }

  unregister(provider: WebsocketProvider): void {
    this.providers.delete(provider);
    wsReconnectCoordinator.unregister(provider);
  }

  private handleTokenRefresh(): void {
    let newToken: string | null = null;
    try {
      newToken = localStorage.getItem('authToken');
    } catch (_) {}

    if (!newToken) return;

    for (const provider of this.providers) {
      // provider.url is a computed getter from provider.params — mutate params directly.
      // providers without a 'token' param were created without auth and should be skipped.
      if (!('token' in provider.params)) continue;
      provider.params['token'] = newToken;
    }
  }
}

export const wsTokenRefresher = new WsTokenRefresher();
